import { createRoute, lazyRouteComponent } from '@tanstack/react-router'

import { guardRoute } from '../auth/guard.ts'
import { parseActionType, type ActionType } from '../messages/index.ts'
import { NAV_PARAM_PATHS } from '../navigation/paths.ts'
import { numericIdParam, parsePositiveInt } from './params.ts'
import { rootRoute } from './root.tsx'

/** One of the player's collections: the showcase, the favourites, or a playlist by its id. */
export type HighlightsCollection = 'showcase' | 'favourites' | number

/** Optional rather than nullable: the router writes a `null` back as `?match=null`. */
export interface PlayerHighlightsSearch {
  /** The match whose view is open; none, the season. */
  readonly match?: number
  /** The collection whose view is open, on the match's view. */
  readonly collection?: HighlightsCollection
  /** The one kind of action the match's view plays. */
  readonly kind?: ActionType
}

export function parseCollection(value: unknown): HighlightsCollection | null {
  if (value === 'showcase' || value === 'favourites') return value
  return parsePositiveInt(value)
}

// A player's highlights, the season and the view of one match (spec #261). A session and no role,
// as the profile: the API answers a reader from outside as if there were none.
export const playerHighlightsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NAV_PARAM_PATHS.playerHighlights,
  params: numericIdParam('personId'),
  staticData: { opensOnHero: true, theater: true },
  validateSearch: (search: Record<string, unknown>): PlayerHighlightsSearch => ({
    match: parsePositiveInt(search.match) ?? undefined,
    collection: parseCollection(search.collection) ?? undefined,
    kind: parseActionType(search.kind) ?? undefined,
  }),
  beforeLoad: ({ context, location }) =>
    guardRoute({ queryClient: context.queryClient, currentPath: location.href }),
  component: lazyRouteComponent(() => import('../pages/player-highlights.tsx')),
})

/** The season of a player's highlights. */
export function playerHighlightsLink(personId: number) {
  return { to: NAV_PARAM_PATHS.playerHighlights, params: { personId }, search: {} } as const
}

/** The view of one match, every kind. */
export function playerMatchHighlightsLink(personId: number, matchId: number) {
  return {
    to: NAV_PARAM_PATHS.playerHighlights,
    params: { personId },
    search: { match: matchId },
  } as const
}

/** The view of one of the player's collections, every kind. */
export function playerCollectionLink(personId: number, collection: HighlightsCollection) {
  return {
    to: NAV_PARAM_PATHS.playerHighlights,
    params: { personId },
    search: { collection },
  } as const
}
